import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { Avatar, NotificationBadge, ActivityToast } from '../ui';
import InstallAppPrompt from '../pwa/InstallAppPrompt';
import { useIsMobile } from '../../hooks/useMediaQuery';

const NAV_TABS = [
  {
    id: 'tasks',
    to: '/dashboard',
    label: 'Tasks',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 11l3 3L22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  {
    id: 'servers',
    to: '/servers',
    label: 'Servers',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <line x1="6" y1="6" x2="6.01" y2="6" />
        <line x1="6" y1="18" x2="6.01" y2="18" />
      </svg>
    ),
  },
  {
    id: 'cybersecurity',
    to: '/cybersecurity',
    label: 'Cybersecurity',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
  },
];

const sidebarHeaderStyle = {
  padding: '1.5rem 1.25rem 1rem',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

function isTasksActive(pathname) {
  return pathname === '/dashboard'
    || pathname.startsWith('/project/')
    || pathname.startsWith('/tasks/');
}

function isServersActive(pathname) {
  return pathname === '/servers' || pathname.startsWith('/servers/');
}

function isCybersecurityActive(pathname) {
  return pathname === '/cybersecurity' || pathname.startsWith('/cybersecurity/');
}

export default function AppLayout({ children }) {
  const { user, logout, projects, notificationsMap } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setSidebarOpen(false);
  }, [isMobile]);

  useEffect(() => {
    document.body.style.overflow = isMobile && sidebarOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, sidebarOpen]);

  const closeSidebar = () => setSidebarOpen(false);
  const onNavClick = () => { if (isMobile) closeSidebar(); };

  const handleLogout = async () => {
    closeSidebar();
    await logout();
    navigate('/login');
  };

  const mobileTitle = (() => {
    if (location.pathname === '/servers') return 'Servers';
    if (location.pathname === '/cybersecurity' || location.pathname.startsWith('/cybersecurity/')) return 'Cybersecurity';
    if (location.pathname.startsWith('/tasks/')) return 'Tasks';
    if (location.pathname === '/dashboard') return 'Tasks';
    if (!location.pathname.startsWith('/project/')) return 'ABGA Dashboard';
    for (const p of projects) {
      if (location.pathname === `/project/${p.id}`) return p.title;
      for (const c of p.children ?? []) {
        if (location.pathname === `/project/${c.id}`) return c.title;
      }
    }
    return 'Project';
  })();

  const totalUnseen = projects.reduce((sum, p) => sum + (notificationsMap[p.id] || 0), 0);

  const renderNavTab = (tab) => {
    const active = tab.id === 'tasks'
      ? isTasksActive(location.pathname)
      : tab.id === 'servers'
        ? isServersActive(location.pathname)
        : isCybersecurityActive(location.pathname);

    return (
      <Link
        key={tab.id}
        to={tab.to}
        onClick={onNavClick}
        className={`sidebar-nav-tab${active ? ' is-active' : ''}`}
      >
        <span className="sidebar-nav-tab__icon">{tab.icon}</span>
        <span className="sidebar-nav-tab__label">{tab.label}</span>
      </Link>
    );
  };

  const sidebar = (
    <aside
      className={`app-sidebar${sidebarOpen ? ' is-open' : ''}`}
      aria-hidden={isMobile && !sidebarOpen}
    >
      <div style={sidebarHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #6C63FF, #a18cd1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" strokeDasharray="4 2"/>
            </svg>
          </div>
          <span style={{
            fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 400,
            color: '#fff', letterSpacing: '-0.02em',
          }}>
            ABGA Dashboard
          </span>
        </div>
      </div>

      <nav className="sidebar-nav" aria-label="Main navigation">
        {NAV_TABS.map(renderNavTab)}
      </nav>

      <InstallAppPrompt />

      <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
          <Avatar initials={user.avatar} size={34} color="#6C63FF" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</p>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          style={{
            width: '100%', padding: '7px', borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.1)', background: 'transparent',
            color: 'rgba(255,255,255,0.45)', fontSize: 13, transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.color = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
        >
          Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="app-shell">
      <header className="app-mobile-header">
        <button
          type="button"
          className="app-menu-btn"
          aria-label={totalUnseen > 0 ? `Open menu, ${totalUnseen} new tasks` : 'Open menu'}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(o => !o)}
          style={{ position: 'relative' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            {sidebarOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <>
                <path d="M4 6h16M4 12h16M4 18h16" />
              </>
            )}
          </svg>
          {!sidebarOpen && totalUnseen > 0 && (
            <NotificationBadge dot style={{ position: 'absolute', top: 4, right: 4 }} />
          )}
        </button>
        <span className="app-mobile-title">{mobileTitle}</span>
      </header>

      <div className="app-body">
        {isMobile && (
          <button
            type="button"
            className={`app-sidebar-backdrop${sidebarOpen ? ' is-visible' : ''}`}
            aria-label="Close menu"
            onClick={closeSidebar}
          />
        )}
        {sidebar}
        <main className="app-main">{children}</main>
      </div>

      <ActivityToast />
    </div>
  );
}
